<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { NButton, NDropdown, NBadge, NAvatar, NDrawer, NDrawerContent } from 'naive-ui'
import { useRouter } from 'vue-router'
import { currentUser, isAuthenticated, isPatient, isProvider, isValidator, logout, getCurrentUser, authToken, isAdmin } from '@/services/auth'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import logoImage from '@/assets/images/logo-medicko.png'
import { dt } from '@/utils/i18n'

const router = useRouter()
const showMobileMenu = ref(false)

// Rest of your existing code without the useI18n import and destructuring
watch(authToken, async (newToken) => {
  if (newToken && !currentUser.value) {
    try {
      await getCurrentUser();
    } catch (e) {
      console.error('Failed to fetch user data after token change:', e);
    }
  }
});

onMounted(async () => {
  if (isAuthenticated.value && !currentUser.value) {
    await getCurrentUser()
  }
})

const goToChat = () => {
  if (isProvider.value) {
    router.push('/provider-chat')
  } else {
    router.push('/patient-chat')
  }
  showMobileMenu.value = false
}

const goHome = () => {
  router.push('/')
  showMobileMenu.value = false
}

const handleLogout = () => {
  logout()
  router.push('/')
  showMobileMenu.value = false
}

const userMenuOptions = computed(() => [
  {
    key: 'profile',
    label: dt('header.myProfile'),
    icon: '👤'
  },
  {
    key: 'bookmarks',
    label: dt('header.bookmarks'),
    icon: '🏥'
  },
  {
    key: 'settings',
    label: dt('header.settings'),
    icon: '⚙️'
  },
  {
    type: 'divider'
  },
  {
    key: 'logout',
    label: dt('header.logout'),
    icon: '🚪'
  }
]);

const handleSelect = (key) => {
  if (key === 'logout') {
    handleLogout()
  } else if (key === 'profile') {
    if (isProvider.value) {
      router.push('/provider-profile')
    } else if (isValidator.value) {
      router.push('/validator-profile')
    } else if (isAdmin.value) {
      router.push('/admin-dashboard')
    } else {
      router.push('/patient-profile')
    }
    showMobileMenu.value = false
  } else if (key === 'bookmarks') {
    router.push('/bookmarks')
    showMobileMenu.value = false
  } else if (key === 'settings') {
    router.push('/settings')
    showMobileMenu.value = false
  }
}

const navigateTo = (path) => {
  router.push(path)
  showMobileMenu.value = false
}

const getUserInitial = () => {
  if (currentUser.value && currentUser.value.full_name) {
    return currentUser.value.full_name.charAt(0).toUpperCase()
  }
  return 'U'
}

const displayName = computed(() => {
  return currentUser.value?.full_name || 'User'
})
</script>

<template>
  <header class="header">
    <div class="container">
      <!-- Desktop Header -->
      <div class="desktop-header">
        <div class="logo">
          <router-link to="/" class="logo-link">
            <img :src="logoImage" alt="Medicko" class="logo" />
          </router-link>
        </div>
        <nav class="nav desktop-nav">
          <router-link to="/" class="nav-link">{{ dt('header.home') }}</router-link>
          <router-link to="/search" class="nav-link">{{ dt('header.search') }}</router-link>
          <router-link to="/about" class="nav-link">{{ dt('header.about') }}</router-link>
          <router-link to="/features" class="nav-link">{{ dt('header.features') }}</router-link>
          <router-link to="/providers" class="nav-link nav-link-provider">
            {{ dt('header.forProviders') }}
          </router-link>
          <router-link to="/patients" class="nav-link nav-link-patient">
            {{ dt('header.forPatients') }}
          </router-link>
        </nav>
        <div class="auth-section">
          <template v-if="isAuthenticated">
            <NButton type="primary" size="small" @click="goToChat" style="margin-right: 12px;">
              🤖 AI Chat
            </NButton>
            <NDropdown trigger="click" :options="userMenuOptions" @select="handleSelect">
              <div class="user-info">
                <span class="user-name">{{ displayName }}</span>
                <NAvatar :size="40" round
                  style="background: linear-gradient(45deg, #667eea, #764ba2); color: white; cursor: pointer">
                  {{ getUserInitial() }}
                </NAvatar>
              </div>
            </NDropdown>
          </template>
          <template v-else>
            <NButton type="default" ghost @click="router.push('/login')" style="margin-right: 10px"
              class="desktop-only">
              {{ dt('header.login') }}
            </NButton>
            <NButton type="primary" @click="router.push('/register')" class="desktop-only">
              {{ dt('header.register') }}
            </NButton>
          </template>
        </div>
        <LanguageSwitcher v-if="!isAuthenticated" class="desktop-only" />
      </div>

      <!-- Mobile Header -->
      <div class="mobile-header">
        <h1 class="logo" @click="goHome" style="cursor: pointer">
          <img :src="logoImage" alt="Medicko" class="logo" />
        </h1>
        <div class="mobile-controls">
          <LanguageSwitcher v-if="!isAuthenticated" class="mobile-language" />
          <button class="menu-button" @click="showMobileMenu = true">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Navigation Drawer -->
    <NDrawer v-model:show="showMobileMenu" placement="right" :width="280">
      <NDrawerContent title="Menu" closable>
        <div class="mobile-menu">
          <div class="mobile-nav-links">
            <div class="mobile-nav-item" @click="navigateTo('/')">{{ dt('header.home') }}</div>
            <div class="mobile-nav-item" @click="navigateTo('/search')">{{ dt('header.search') }}</div>
            <div class="mobile-nav-item" @click="navigateTo('/about')">{{ dt('header.about') }}</div>
            <div class="mobile-nav-item" @click="navigateTo('/features')">{{ dt('header.features') }}</div>
          </div>

          <div class="mobile-auth" v-if="!isAuthenticated">
            <NButton block type="default" @click="navigateTo('/login')" style="margin-bottom: 10px">
              {{ dt('header.login') }}
            </NButton>
            <NButton block type="primary" @click="navigateTo('/register')">
              {{ dt('header.register') }}
            </NButton>
          </div>

          <div class="mobile-user-menu" v-else>
            <div class="mobile-user-header">
              <NAvatar :size="40" round style="background: linear-gradient(45deg, #667eea, #764ba2); color: white;">
                {{ getUserInitial() }}
              </NAvatar>
              <span class="mobile-user-name">{{ displayName }}</span>
            </div>
            <div class="mobile-nav-item" @click="navigateTo(isProvider ? '/provider-profile' : '/patient-profile')">
              <span class="mobile-menu-icon">👤</span>
              {{ isProvider ? dt('header.myProfile') : dt('header.myProfile') }}
            </div>
            <div class="mobile-nav-item" @click="navigateTo('/bookmarks')">
              <span class="mobile-menu-icon">🏥</span> {{ dt('header.bookmarks') }}
            </div>
            <div class="mobile-nav-item" @click="goToChat">
              <span class="mobile-menu-icon">🤖</span> AI Chat
            </div>
            <div class="mobile-nav-item" @click="navigateTo('/settings')">
              <span class="mobile-menu-icon">⚙️</span> {{ dt('header.settings') }}
            </div>
            <div class="mobile-nav-item logout" @click="handleLogout">
              <span class="mobile-menu-icon">🚪</span> {{ dt('header.logout') }}
            </div>
          </div>
        </div>
      </NDrawerContent>
    </NDrawer>
  </header>
</template>

<style scoped>
.header {
  background: #ffffff;
  /* Light background */
  color: #333333;
  /* Dark text */
  padding: 1rem 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.header .container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.desktop-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  width: 160px;
  font-size: 2rem;
  font-weight: bold;
  color: white;
}

.nav {
  display: flex;
  gap: 2rem;
}

.nav-link-provider {
  color: #27ae60 !important;
  /* Your green color */
  font-weight: 600;
}

.nav-link-provider:hover {
  color: #229954 !important;
  /* Darker green on hover */
}

.nav-link-patient {
  color: #3498db !important;
  /* Your green color */
  font-weight: 600;
}

.nav-link-patient:hover {
  color: #3498db !important;
  /* Darker green on hover */
}

.auth-section {
  display: flex;
  align-items: center;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.user-name {
  font-weight: 500;
}

.nav-link {
  color: #333333;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.nav-link:hover {
  color: #555555;
  text-decoration: underline;
}

/* Mobile Header Styles */
.mobile-header {
  display: none;
  justify-content: space-between;
  align-items: center;
}

.mobile-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.menu-button {
  background: none;
  border: none;
  color: #333333;
  cursor: pointer;
  padding: 5px;
}

/* Mobile Menu Styles */
.mobile-menu {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.mobile-nav-links {
  display: flex;
  flex-direction: column;
}

.mobile-nav-item {
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.mobile-nav-item:hover {
  color: #333333;
}

.mobile-user-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.mobile-user-name {
  font-weight: 500;
  font-size: 1.1rem;
}

.mobile-menu-icon {
  margin-right: 10px;
}

.logout {
  color: #d32f2f;
}

.mobile-language {
  margin-right: 10px;
}

/* Media Queries */
@media (max-width: 768px) {
  .desktop-header {
    display: none;
  }

  .mobile-header {
    display: flex;
  }

  .desktop-only {
    display: none;
  }
}

@media (min-width: 769px) {
  .mobile-header {
    display: none;
  }
}
</style>